import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Play, Pause, Volume2, VolumeX, Download, Share2, Heart,
  Sparkles, Zap, Music, Home, Search, Bell, Library,
  Compass, Settings, User, SkipForward, SkipBack,
  Shuffle, Repeat, ListMusic, Maximize2, ArrowRight,
  TrendingUp, Clock, Edit3, Album, ChevronDown, ChevronUp,
  Plus, X, MoreHorizontal, Copy, Upload, Scissors, FileAudio,
  Video, AudioWaveform, Music2, Disc3, Layers,
  LayoutDashboard, MessageSquare, ImageIcon
} from 'lucide-react';
import { generateMusic, checkStatus } from '../services/sunoMusicService';
import * as contentService from '../services/contentService';
import { pollJob } from '../services/contentService';
import { HistorySidebar } from './HistorySidebar';
import { useAuth } from '../contexts/AuthContext';

// Import Google Fonts
const FONT_LINK = document.createElement('link');
FONT_LINK.href = 'https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@400;500;600;700&display=swap';
FONT_LINK.rel = 'stylesheet';
if (!document.querySelector('link[href*="Plus+Jakarta+Sans"]')) {
  document.head.appendChild(FONT_LINK);
}

const COLORS = {
  primary: '#8b5cf6',
  primaryHover: '#7c3aed',
  background: '#f5f5f7',
  surface: '#ffffff',
  surfaceHover: '#f9fafb',
  border: '#e5e7eb',
  borderLight: '#f3f4f6',
  text: '#1f2937',
  textSecondary: '#6b7280',
  textTertiary: '#9ca3af',
  accent: '#8b5cf6',
  accentLight: '#ede9fe',
};

const getArtwork = (seed = '') => {
  const gradients = [
    'linear-gradient(135deg, #6b38d4 0%, #8455ef 100%)',
    'linear-gradient(135deg, #8455ef 0%, #a855f7 100%)',
    'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
    'linear-gradient(135deg, #7c3aed 0%, #9333ea 100%)',
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  return gradients[Math.abs(h) % gradients.length];
};

const seedBars = (seed = '', n = 5) => {
  let h = 5381;
  for (let i = 0; i < seed.length; i++) h = ((h << 5) + h) + seed.charCodeAt(i);
  return Array.from({ length: n }, (_, i) => {
    const v = Math.abs(Math.sin((h + i) * 0.41 + i * 1.07) * 0.45 + Math.sin(i * 0.55 + h * 0.009) * 0.3 + 0.28);
    return Math.max(0.12, Math.min(1, v));
  });
};

const WaveBar = ({ value, isActive }) => (
  <motion.div
    className="flex-1 rounded-full"
    style={{
      backgroundColor: isActive ? COLORS.primary : `${COLORS.primary}33`,
    }}
    animate={{
      height: isActive ? [`${value * 100}%`, `${Math.max(15, ((value * 100) + 35) % 100)}%`, `${value * 100}%`] : `${value * 100}%`,
    }}
    transition={isActive ? { duration: 0.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 0.3 }}
  />
);

const MiniWaveform = ({ seed, isActive = false }) => {
  const values = seedBars(seed, 5);
  return (
    <div className="flex items-end gap-1 h-6">
      {values.map((v, i) => (
        <WaveBar key={i} value={v} isActive={isActive} />
      ))}
    </div>
  );
};

const fmt = (s) => {
  if (!s || isNaN(s)) return '0:00';
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
};

const QUICK_PROMPTS = ['808 Bass', 'Lo-fi', 'Cyberpunk', 'Synthwave', 'Jazz Fusion'];
const MUSIC_STYLES = [
  { id: 'pop', label: 'Pop' },
  { id: 'rock', label: 'Rock' },
  { id: 'electronic', label: 'Electronic' },
  { id: 'hiphop', label: 'Hip-Hop' },
  { id: 'jazz', label: 'Jazz' },
  { id: 'classical', label: 'Classical' },
  { id: 'rnb', label: 'R&B' },
  { id: 'country', label: 'Country' },
  { id: 'reggae', label: 'Reggae' },
  { id: 'blues', label: 'Blues' },
  { id: 'metal', label: 'Metal' },
  { id: 'indie', label: 'Indie' },
  { id: 'ambient', label: 'Ambient' },
  { id: 'lofi', label: 'Lo-Fi' },
  { id: 'synthwave', label: 'Synthwave' },
];

export const MusicGenerationWorkspace = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [prompt, setPrompt] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [isVocal, setIsVocal] = useState(true);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [styleInput, setStyleInput] = useState('');
  const [model, setModel] = useState('V4');
  const [generations, setGenerations] = useState([]);
  const [isGen, setIsGen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [selected, setSelected] = useState(null);
  const [liked, setLiked] = useState(new Set());
  const [hasMore, setHasMore]         = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dbOffset, setDbOffset]       = useState(0);
  
  // Simple/Advanced mode
  const [mode, setMode] = useState('simple');
  const [showStyles, setShowStyles] = useState(true);
  
  // Advanced features
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [timestampedLyrics, setTimestampedLyrics] = useState(null);
  const [uploadMode, setUploadMode] = useState(null);

  const [nowPlaying, setNowPlaying] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currTime, setCurrTime] = useState(0);
  const [totalDur, setTotalDur] = useState(0);
  const [vol, setVol] = useState(0.7);
  const [muted, setMuted] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const audioRef = useRef(null);
  const uid = 'demo-user';

  // ── Load DB tracks whenever user profile becomes available ────────────────
  // useEffect([]) alone is NOT enough - userProfile is async (auth may not be ready on first render)
  const loadGensRef = useRef(false);
  useEffect(() => {
    if (location.state?.initialPrompt) setPrompt(location.state.initialPrompt);
  }, []);  // set prompt on mount only

  useEffect(() => {
    if (userProfile?.id && !loadGensRef.current) {
      loadGensRef.current = true;
      loadGens();
    }
  }, [userProfile?.id]); // re-runs when auth completes

  // Auto-trigger generation when arriving from landing page with initialPrompt
  // CRITICAL: must wait for userProfile.id - otherwise generation runs under 'anonymous'
  // user ID and the track is saved to the wrong account, never appearing in loadGens.
  const hasAutoGenerated = useRef(false);
  useEffect(() => {
    if (
      location.state?.initialPrompt &&
      !hasAutoGenerated.current &&
      !isGen &&
      prompt.trim() &&
      userProfile?.id     // ← REQUIRED: ensures correct user ID in generateMusic request
    ) {
      hasAutoGenerated.current = true;
      // Clear history state so page refresh doesn't re-trigger generation
      window.history.replaceState({}, '', window.location.href);
      const timer = setTimeout(() => handleGenerate(), 400);
      return () => clearTimeout(timer);
    }
  }, [prompt, userProfile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGens = async (offset = 0, append = false) => {
    try {
      if (!userProfile?.id) return;
      const { tracks, has_more } = await contentService.getMusic(userProfile.id, offset, 20);
      if (!tracks.length && !append) return;

      setHasMore(has_more);
      setDbOffset(offset + tracks.length);

      const mapped = tracks.map(t => {
        const r2Url       = t.metadata?.publicUrl || t.publicUrl || null;
        const originalUrl = t.metadata?.originalUrl || null;
        const keiTaskId   = t.metadata?.keiTaskId
          || t.r2_path?.match(/musics\/([^/_]+)/)?.[1]
          || null;
        return {
          id:              t.id,
          audio_url:       r2Url || originalUrl,
          original_url:    originalUrl,
          image_url:       t.metadata?.imageUrl || null,
          title:           t.metadata?.title || t.prompt?.substring(0, 40) || 'Untitled',
          lyrics:          t.metadata?.lyrics  || '',
          style:           t.metadata?.style   || 'AI Generated',
          prompt:          t.prompt,
          model:           t.model,
          duration:        t.duration,
          is_instrumental: t.is_instrumental,
          suno_audio_id:   t.metadata?.sunoAudioId || null,
          kei_task_id:     keiTaskId,
          status:          'completed',
          fromDb:          true,    // flag: loaded from D1 (not in-session)
        };
      });

      // MERGE: keep in-session tracks that are not yet in DB
      // Check by BOTH audio_url and original_url to handle R2 vs Kei.AI URL differences
      // Deduplicate by URL - safety net for any duplicate rows in DB
      const seenUrls = new Set();
      const deduped = mapped.filter(t => {
        const key = t.original_url || t.audio_url;
        if (!key || seenUrls.has(key)) return false;
        seenUrls.add(key);
        return true;
      });

      setGenerations(prev => {
        const dbUrls = new Set([
          ...deduped.map(t => t.original_url),
          ...deduped.map(t => t.audio_url),
        ].filter(Boolean));
        // In-session tracks: no fromDb flag, not skeletons, audio URL not yet in DB
        const freshTracks = prev.filter(t =>
          !t.fromDb &&
          t.status !== 'generating' &&
          !dbUrls.has(t.original_url) &&
          !dbUrls.has(t.audio_url)
        );
        if (append) {
          // Pagination: add new page tracks after existing ones (avoid re-adding duplicates)
          const existingIds = new Set(prev.map(t => t.id));
          const newTracks = deduped.filter(t => !existingIds.has(t.id));
          return [...prev.filter(t => t.status !== 'generating'), ...newTracks];
        }
        return [...freshTracks, ...deduped];
      });
      // ⛔ Do NOT auto-select - lyrics panel opens only when user explicitly plays/selects a track

    } catch (e) {
      console.warn('[MusicWorkspace] D1 load error:', e);
    }
  };



  const handleGenerate = async () => {
    let inputText = mode === 'simple' ? prompt : lyrics;
    if (!inputText.trim() || isGen) return;
    inputText = inputText.trim();
    const useCustomMode = mode === 'advanced';
    const maxLength = useCustomMode ? (model.includes('V4_5') || model.includes('V5') ? 5000 : 3000) : 500;
    if (inputText.length > maxLength) {
      inputText = inputText.substring(0, maxLength);
      alert(`Prompt truncated to ${maxLength} characters.`);
    }
    setIsGen(true);
    setProgress({ msg: 'Submitting…', pct: 10 });
    // Show 2 skeleton placeholders at the top of the list immediately
    const skeletonIds = [`skel-${Date.now()}-0`, `skel-${Date.now()}-1`];
    setGenerations(prev => [
      { id: skeletonIds[0], status: 'generating', title: 'Generating…', audio_url: null, image_url: null },
      { id: skeletonIds[1], status: 'generating', title: 'Generating…', audio_url: null, image_url: null },
      ...prev.filter(t => t.status !== 'generating'),  // remove any previous skeletons
    ]);
    try {
      const opts = {
        prompt: inputText,
        model,
        instrumental: !isVocal,
        customMode: useCustomMode,
        userId: userProfile?.id,           // used by service to set x-user-id header
        ...(useCustomMode && {
          style: selectedStyles.join(', ') || 'AI Generated Music',
          title: 'Untitled Track'
        })
      };
      const taskId = await generateMusic(opts);
      setProgress({ msg: 'Generating your track…', pct: 25 });
      console.log('[MusicGen] taskId:', taskId);

      // ── Strategy 1: D1 Worker polling (webhook-updated) ──────────────────
      // Falls back after 8 stuck non-terminal polls (~40s) just like Video
      let audioUrls        = null;
      let capturedSunoData = null;   // hoisted - accessible after either poll path
      let workerDone       = false;
      let workerNullCount  = 0;
      let workerStuckCount = 0;
      const MAX_NULLS  = 3;
      const MAX_STUCK  = 8;

      if (userProfile?.id) {
        try {
          const job = await pollJob(userProfile.id, taskId, {
            intervalMs: 5000,
            maxAttempts: 72,
            onProgress: (j, attempt) => {
              const st = j?.status;
              console.log(`[MusicGen] D1 poll attempt ${attempt}, status:`, st);
              setProgress({ msg: 'Composing your track…', pct: Math.min(25 + attempt * 1.2, 75) });

              if (j === null || j === undefined) {
                workerNullCount++;
              } else if (st !== 'completed' && st !== 'failed') {
                workerStuckCount++;
              }

              if (workerNullCount >= MAX_NULLS || workerStuckCount >= MAX_STUCK) {
                console.log(`[MusicGen] Falling back to direct poll (nulls=${workerNullCount} stuck=${workerStuckCount})`);
                throw new Error('__FALLBACK__');
              }
            },
          });
          // D1 job completed via webhook.
          // D1 only stores ONE result_url but music generates 2 tracks.
          // Fetch fresh data from Kei.AI status endpoint to get ALL tracks + metadata.
          const primary = job?.resultUrl || job?.result_url;
          if (primary) {
            console.log('[MusicGen] D1 confirmed completion. Fetching all tracks from Kei.AI…');
            setProgress({ msg: 'Fetching track details…', pct: 85 });
            try {
              const freshResp = await checkStatus(taskId);
              console.log('[MusicGen] Kei.AI fresh status:', freshResp?.status, '| tracks:', freshResp?.audioUrls?.length);
              if ((freshResp?.status === 'completed' || freshResp?.status === 'first') && freshResp?.audioUrls?.length) {
                audioUrls        = freshResp.audioUrls;
                capturedSunoData = freshResp.sunoData || null;
                workerDone       = true;
              } else {
                // Kei.AI not quite ready - use single D1 URL as fallback
                audioUrls  = [primary];
                workerDone = true;
              }
            } catch (freshErr) {
              console.warn('[MusicGen] Fresh Kei.AI fetch failed, using D1 URL:', freshErr.message);
              audioUrls  = [primary];
              workerDone = true;
            }
          }
        } catch (err) {
          if (err.message !== '__FALLBACK__' && !err.message?.includes('timed out')) {
            console.warn('[MusicGen] D1 poll error:', err.message);
          }
        }
      }

      if (!workerDone || !audioUrls) {
        let attempt = 0;
        const maxAttempts = 80;
        while (attempt < maxAttempts && !audioUrls) {
          attempt++;
          setProgress({ msg: 'Composing your track…', pct: Math.min(30 + attempt * 0.7, 92) });
          await new Promise(r => setTimeout(r, 5000));
          try {
            const resp = await checkStatus(taskId);
            console.log(`[MusicGen] Direct poll ${attempt}:`, JSON.stringify(resp).substring(0, 300));

            // Always capture sunoData - Kei.AI real format uses snake_case (audio_url, image_url)
            if (resp?.sunoData?.length) {
              capturedSunoData = resp.sunoData;
              console.log('[MusicGen] sunoData captured at poll', attempt, ':', capturedSunoData.length, 'tracks');
            }

            // ── Real Kei.AI format: status 'completed' or 'first' ──
            if ((resp?.status === 'completed' || resp?.status === 'first') && resp?.audioUrls?.length) {
              audioUrls = resp.audioUrls;
              if (resp.sunoData?.length) capturedSunoData = resp.sunoData;
              break;
            }
            // ── Failed ──
            if (resp?.status === 'failed' || resp?.success === false) {
              throw new Error(resp?.error || 'Music generation failed');
            }
            // ── Plain URL array fallback ──
            if (Array.isArray(resp) && typeof resp[0] === 'string' && resp[0].startsWith('http')) {
              audioUrls = resp;
              break;
            }
          } catch (pollErr) {
            if (pollErr.message?.includes('failed') || pollErr.message?.includes('FAILED')) throw pollErr;
            console.warn(`[MusicGen] Direct poll ${attempt} error (retrying):`, pollErr.message);
          }
        }
      }

      if (!audioUrls?.length) throw new Error('Generation completed but no audio URL returned');

      // ── Build full tracks - sunoData uses snake_case (per Kei.AI docs) ─────
      // audio_url = original Kei.AI temp URL (plays immediately)
      // original_url = same, kept as fallback if R2 upload became corrupted
      const sunoData = capturedSunoData || null;
      const tracksToAdd = audioUrls.map((url, i) => {
        const sd = sunoData?.[i];
        return {
          id:              i === 0 ? taskId : `${taskId}-${i}`,
          audio_url:       url,
          original_url:    url,
          image_url:       sd?.image_url  || null,
          title:           sd?.title      || `Track ${i + 1}`,
          lyrics:          sd?.prompt     || '',
          style:           sd?.tags       || selectedStyles.join(', ') || 'AI Generated',
          prompt:          inputText,
          model,
          duration:        sd?.duration   || null,
          suno_audio_id:   sd?.id         || null,
          kei_task_id:     taskId,          // ← Kei.AI music task ID (required for Create Video)
          is_instrumental: !isVocal,
          status:          'completed',
        };
      });

      // Replace skeletons with real tracks; keep everything else
      setGenerations(prev => {
        const withoutSkeletons = prev.filter(t => t.status !== 'generating');
        return [...tracksToAdd.slice().reverse(), ...withoutSkeletons];
      });
      setSelected(tracksToAdd[0]);
      // Auto-play the first new track so the player updates immediately
      playTrack(tracksToAdd[0]);

      // Webhook handles all DB persistence - no frontend saveMusic needed
      // (removing frontend saves prevents duplicate rows: webhook saves on 'complete' callback)


      setIsGen(false);
      setProgress({ msg: '✨ Complete!', pct: 100 });
      setTimeout(() => setProgress(null), 2000);

      // After 12s, refresh from DB - gives webhook time to save before re-fetching.
      // New tracks are already in state; merge logic keeps them at top if not yet in DB.
      setTimeout(() => {
        loadGensRef.current = false;
        loadGens();
      }, 12000);

    } catch (e) {
      setIsGen(false);
      // Clean up skeletons on error too
      setGenerations(prev => prev.filter(t => t.status !== 'generating'));
      setProgress({ msg: e.message, pct: 0, err: true });
    }
  };






  const poll = async (tid, gid) => {
    let att = 0;
    const go = async () => {
      try {
        att++;
        const d = await checkStatus(tid);
        const s = d.status || 'PENDING';
        
        // Check if this is a video generation task
        const pendingVideoData = sessionStorage.getItem('pending_video_track');
        const isVideoTask = pendingVideoData !== null;
        
        if (s === 'PENDING') {
          setProgress({ msg: isVideoTask ? 'Creating video…' : 'Composing melody…', pct: Math.min(20 + att * 2, 60) });
          setTimeout(go, 5000);
        } else if (s === 'TEXT_SUCCESS') {
          const tracks = d.response?.sunoData || [];
          if (tracks.length > 0 && (tracks[0]?.audioUrl || tracks[0]?.audio_url)) {
            await done(gid, tracks);
            return;
          }
          setProgress({ msg: 'Generating audio…', pct: Math.min(62 + att * 2, 90) });
          setTimeout(go, 5000);
        } else if (s === 'SUCCESS' || s === 'FIRST_SUCCESS') {
          const arr = d.response?.sunoData || d.response?.data || [];
          
          // Handle video generation completion
          if (isVideoTask && arr.length > 0 && arr[0]?.videoUrl) {
            const videoData = JSON.parse(pendingVideoData);
            const video = arr[0];
            
            // Save video to localStorage (video generation is a side feature)
            const videos = JSON.parse(localStorage.getItem('music_videos') || '[]');
            videos.push({
              id: `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              video_url: video.videoUrl || video.video_url,
              thumbnail_url: video.thumbnailUrl || video.thumbnail_url,
              title: videoData.track_title,
              source_track_title: videoData.track_title,
              source_track_id: videoData.track_id,
              image_url: videoData.track_image,
              created_at: new Date().toISOString()
            });
            localStorage.setItem('music_videos', JSON.stringify(videos));
            sessionStorage.removeItem('pending_video_track');
            
            setIsGen(false);
            setProgress(null);
            alert(`Music video created successfully!\n\nView it in the "Music Videos" section.`);
            return;
          }
          
          // Handle music generation completion
          console.log('🎵 Status:', s, 'Tracks:', arr.length);
          if (Array.isArray(arr) && arr.length > 0) {
            console.log('🎵 First track:', arr[0]);
            const hasAudio = arr.some(track => track?.audioUrl || track?.audio_url || track?.streamAudioUrl);
            console.log('🎵 Has audio:', hasAudio);
            if (hasAudio) {
              await done(gid, arr);
              return;
            }
          }
          setProgress({ msg: 'Finalizing…', pct: Math.min(85 + att, 95) });
          setTimeout(go, 5000);
        } else if (s.includes('FAILED') || s.includes('ERROR')) {
          sessionStorage.removeItem('pending_video_track');
          throw new Error(d.errorMessage || 'Failed');
        } else if (att < 120) setTimeout(go, 5000);
      } catch (e) {
        setIsGen(false);
        setProgress({ msg: e.message, pct: 0, err: true });
      }
    };
    go();
  };

  const done = async (gid, tracks) => {
    // Suno API returns 2 tracks per generation
    const tracksArray = Array.isArray(tracks) ? tracks : [tracks];
    
    console.log('💾 Saving tracks:', tracksArray.length, tracksArray);
    
    // Save both tracks
    for (let i = 0; i < tracksArray.length; i++) {
      const t = tracksArray[i];
      const trackGid = i === 0 ? gid : `${gid}-${i}`;
      
      const trackData = {
        audio_url: t.audioUrl || t.sourceAudioUrl || t.streamAudioUrl || t.audio_url,
        image_url: t.imageUrl || t.streamImageUrl || t.image_url,
        duration: t.duration,
        title: t.title || `Untitled ${i + 1}`,
        lyrics: t.prompt || t.lyrics || '',
        style: t.tags || t.style || '',
        model: t.type || t.model || 'V4',
        suno_audio_id: t.id, // Store the Suno audio ID for video generation and other operations
      };
      
      console.log(`💾 Track ${i + 1} data:`, trackData);
      
      if (i === 0) {
        // Update local state
        setGenerations(prev => [{ id: trackGid, ...trackData, status: 'completed' }, ...prev]);
        
        // Save to D1 + R2 for authenticated users
        if (userProfile?.id) {
          contentService.saveMusic(userProfile.id, {
            prompt: trackData.lyrics || prompt,
            model: trackData.model,
            url: trackData.audio_url,
            duration: trackData.duration,
            isInstrumental: !isVocal,
            metadata: {
              title: trackData.title,
              style: trackData.style,
              imageUrl: trackData.image_url,
              sunoAudioId: trackData.suno_audio_id,
            }
          }).catch(err => console.warn('[MusicWorkspace] D1 save error:', err));
        }
      } else {
        // Additional tracks (Suno returns 2 per generation): add to local state
        setGenerations(prev => [...prev, { id: trackGid, ...trackData, status: 'completed' }]);
        // Also save the second track to D1
        if (userProfile?.id) {
          contentService.saveMusic(userProfile.id, {
            prompt: trackData.lyrics || prompt,
            model: trackData.model,
            url: trackData.audio_url,
            duration: trackData.duration,
            isInstrumental: !isVocal,
            metadata: {
              title: trackData.title,
              style: trackData.style,
              imageUrl: trackData.image_url,
              sunoAudioId: trackData.suno_audio_id,
            }
          }).catch(err => console.warn('[MusicWorkspace] D1 save error (track 2):', err));
        }
      }
    }
    
    setIsGen(false);
    setProgress({ msg: '✨ Complete!', pct: 100 });
    setTimeout(() => setProgress(null), 2000);
  };

  useEffect(() => {
    if (!nowPlaying || !audioRef.current) return;

    const audio = audioRef.current;
    const audioUrl = nowPlaying.audio_url || '';
    console.log('🎵 Loading audio:', audioUrl);
    audio.src = audioUrl;

    const handleCanPlay = () => {
      console.log('🎵 Audio can play, attempting to play...');
      audio.play()
        .then(() => { console.log('✅ Audio playing successfully'); setPlaying(true); })
        .catch((err) => { console.error('❌ Play error:', err); setPlaying(false); });
    };

    const handleError = (e) => {
      console.error('❌ Audio load error:', e);
      console.error('Audio error code:', audio.error?.code);
      console.error('Audio error message:', audio.error?.message);

      // Auto-fallback: R2 file might be corrupted - try the original Kei.AI URL
      const fallback = nowPlaying?.original_url;
      if (fallback && audio.src !== fallback) {
        console.warn('🎵 R2 playback failed, falling back to original URL:', fallback);
        audio.src = fallback;
        audio.addEventListener('canplay', handleCanPlay, { once: true });
        audio.load();
      } else {
        setPlaying(false);
      }
    };

    audio.addEventListener('canplay', handleCanPlay, { once: true });
    audio.addEventListener('error', handleError, { once: true });
    audio.load();

    return () => {
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
    };
  }, [nowPlaying]);


  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : vol;
  }, [vol, muted]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const playTrack = (t) => {
    const audioUrl = t.audio_url || t.audioUrl || t.streamAudioUrl;
    if (!audioUrl) {
      console.error('❌ Track has no audio URL:', t);
      return;
    }
    const trackWithAudio = { ...t, audio_url: audioUrl };

    if (nowPlaying?.id === t.id && playing) {
      togglePlay();
    } else {
      setNowPlaying(trackWithAudio);
      setPlaying(true);
      // Open lyrics panel for this track when played
      setSelected(trackWithAudio);
    }
  };

  const toggleLike = (id) =>
    setLiked((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const dlTrack = (t) => {
    if (!t?.audio_url) return;
    window.open(
      `/api/suno/download?url=${encodeURIComponent(t.audio_url)}&title=${encodeURIComponent(t.title || 'Untitled')}`,
      '_blank'
    );
  };

  const pct = totalDur ? (currTime / totalDur) * 100 : 0;

  // Handle audio file uploads
  const handleAudioUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsGen(true);
    setProgress({ msg: `Uploading and processing...`, pct: 10 });

    try {
      let taskId;
      const { uploadAndCover, uploadAndExtend, addInstrumental, addVocals } = await import('../services/sunoMusicService');

      switch (type) {
        case 'cover':
          taskId = await uploadAndCover(file, { prompt: prompt || 'Cover version', model, style: selectedStyles.join(', ') });
          break;
        case 'extend':
          taskId = await uploadAndExtend(file, { prompt: prompt || 'Extended version', model });
          break;
        case 'instrumental':
          taskId = await addInstrumental(file);
          break;
        case 'vocals':
          taskId = await addVocals(file);
          break;
      }

      if (taskId) {
        poll(taskId, null);
      }
    } catch (error) {
      console.error('Upload error:', error);
      setIsGen(false);
      setProgress(null);
      alert(`Upload failed: ${error.message}`);
    }
  };

  // Handle advanced track actions
  const handleTrackAction = async (track, action) => {
    setShowContextMenu(null);

    // Check if track has Suno audio ID (required for all advanced features)
    const audioId = track.suno_audio_id;
    if (!audioId && action !== 'lyrics') {
      alert('This track does not have a Suno audio ID. Please generate a new track to use advanced features.');
      return;
    }

    setIsGen(true);
    setProgress({ msg: `Processing ${action}...`, pct: 10 });

    try {
      const {
        extendMusic, separateVocals, convertToWAV, generateMIDI,
        createMusicVideo, checkMusicVideoStatus, generateCover, getTimestampedLyrics
      } = await import('../services/sunoMusicService');

      let taskId;
      switch (action) {
        case 'extend':
          taskId = await extendMusic({ audioId, model, defaultParamFlag: true });
          break;
        case 'separate':
          taskId = await separateVocals(audioId);
          break;
        case 'wav':
          taskId = await convertToWAV(audioId);
          break;
        case 'midi':
          taskId = await generateMIDI(audioId);
          break;

        case 'video': {
          // ── Music Video Generation ──────────────────────────────────────
          const confirmed = window.confirm(
            `Create a music video for "${track.title || 'this track'}"?\n\n` +
            `Kei.AI will generate an MP4 video synced to your track.\n` +
            `This typically takes 2–5 minutes.\n\n` +
            `Click OK to start.`
          );
          if (!confirmed) { setIsGen(false); setProgress(null); return; }

          setProgress({ msg: '🎬 Starting music video generation…', pct: 10 });

          const mvTaskId = await createMusicVideo(
            audioId,
            track.kei_task_id,   // ← Kei.AI music task ID, required by /mp4/generate
            userProfile?.id,
            track.title
          );
          if (!mvTaskId) throw new Error('No taskId returned for music video');

          // Dedicated polling loop using /api/suno/video-status/:taskId
          let videoUrl = null;
          let attempt  = 0;
          const maxAttempts = 80;  // ~6.5 min @ 5s each
          while (attempt < maxAttempts && !videoUrl) {
            attempt++;
            setProgress({ msg: `🎬 Creating music video${'.'.repeat((attempt % 3) + 1)}`, pct: Math.min(10 + attempt * 1.1, 92) });
            await new Promise(r => setTimeout(r, 5000));
            try {
              const resp = await checkMusicVideoStatus(mvTaskId);
              console.log(`[MusicVideo] poll ${attempt}:`, resp?.status, resp?.videoUrl);
              if (resp?.status === 'completed' && resp?.videoUrl) {
                videoUrl = resp.videoUrl;
                break;
              }
              if (resp?.status === 'failed' || resp?.success === false) {
                throw new Error(resp?.error || 'Music video generation failed');
              }
            } catch (pollErr) {
              if (pollErr.message?.includes('failed')) throw pollErr;
              console.warn(`[MusicVideo] poll ${attempt} error (retrying):`, pollErr.message);
            }
          }

          if (!videoUrl) throw new Error('Music video generation timed out - try again later');

          // Update the track in state with the video URL
          const updatedTrack = { ...track, video_url: videoUrl };
          setGenerations(prev => prev.map(t => t.id === track.id ? updatedTrack : t));
          setSelected(updatedTrack);

          setIsGen(false);
          setProgress({ msg: '🎬 Music video ready!', pct: 100 });
          setTimeout(() => setProgress(null), 3000);
          return;
        }

        case 'cover':
          taskId = await generateCover(audioId);
          break;
        case 'lyrics':
          const lyrics = await getTimestampedLyrics(audioId);
          setTimestampedLyrics(lyrics);
          setSelected(track);
          setIsGen(false);
          setProgress(null);
          return;
      }

      if (taskId) {
        poll(taskId, null);
      }
    } catch (error) {
      console.error('Action error:', error);
      setIsGen(false);
      setProgress(null);
      alert(`Action failed: ${error.message}`);
    }
  };


  return (
    <div className="flex flex-col h-screen w-full overflow-hidden" style={{ fontFamily: "'Inter', sans-serif", background: COLORS.background }}>
      <audio
        ref={audioRef}
        onTimeUpdate={() => setCurrTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setTotalDur(audioRef.current?.duration || 0)}
        onEnded={() => setPlaying(false)}
      />

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Column 1: Sidebar */}
        <aside className="w-64 flex-shrink-0 border-r flex flex-col h-full py-6 px-4" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
          <div className="flex items-center gap-3 mb-8">
            <img src="/DAGGPT-01.jpg" alt="DAGGPT" className="w-10 h-10 rounded-lg object-cover" />
            <div>
              <h1 className="text-base font-bold" style={{ color: COLORS.text }}>DAGGPT</h1>
              <p className="text-xs" style={{ color: COLORS.textSecondary }}>Music Studio</p>
            </div>
          </div>

          <nav className="space-y-1 flex-1">
            <NavItem icon={Home} label="Home" active />
            <NavItem icon={LayoutDashboard} label="Dashboard" href="/dashboard" />
            <NavItem icon={MessageSquare} label="Chat" onClick={() => navigate('/chat')} />
            <NavItem icon={ImageIcon} label="Image" onClick={() => navigate('/image-generation')} />
            <NavItem icon={Video} label="Video" onClick={() => navigate('/video-generation')} />
          </nav>
        </aside>

        {/* Column 2: Creation Panel */}
        <main className="w-[380px] flex-shrink-0 border-r flex flex-col overflow-hidden" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
          <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
            <h2 className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: COLORS.textSecondary }}>Describe Your Sound</h2>
            
            {/* Simple/Advanced Toggle */}
            <div className="flex gap-2 p-1 rounded-lg" style={{ background: COLORS.borderLight }}>
              <button
                onClick={() => setMode('simple')}
                className="flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all"
                style={{
                  background: mode === 'simple' ? COLORS.surface : 'transparent',
                  color: mode === 'simple' ? COLORS.text : COLORS.textSecondary,
                  boxShadow: mode === 'simple' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Simple
              </button>
              <button
                onClick={() => setMode('advanced')}
                className="flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all"
                style={{
                  background: mode === 'advanced' ? COLORS.surface : 'transparent',
                  color: mode === 'advanced' ? COLORS.text : COLORS.textSecondary,
                  boxShadow: mode === 'advanced' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Advanced
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-6 space-y-4">

            {/* Simple Mode - Prompt Only */}
            {mode === 'simple' && (
              <div className="rounded-xl border" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Inspiring noise rock song about the hustle"
                  disabled={isGen}
                  className="w-full p-4 bg-transparent resize-none outline-none text-sm"
                  style={{ color: COLORS.text, minHeight: '120px' }}
                />
              </div>
            )}

            {/* Advanced Mode - Lyrics */}
            {mode === 'advanced' && (
              <div className="rounded-xl border" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
                <textarea
                  value={lyrics}
                  onChange={(e) => setLyrics(e.target.value)}
                  placeholder="Write some lyrics or a prompt - or leave blank for instrumental"
                  disabled={isGen}
                  className="w-full p-4 bg-transparent resize-none outline-none text-sm"
                  style={{ color: COLORS.text, minHeight: '180px' }}
                />
              </div>
            )}

            {/* Styles Section - Collapsible */}
            <div className="rounded-xl border" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
              <button
                onClick={() => setShowStyles(!showStyles)}
                className="w-full px-4 py-3 flex items-center justify-between text-sm font-medium"
                style={{ color: COLORS.text }}
              >
                <span>Styles</span>
                {showStyles ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showStyles && (
                <div className="px-4 pb-4 border-t" style={{ borderColor: COLORS.border }}>
                  <div className="mt-3">
                    <div className="flex flex-wrap gap-2">
                      {MUSIC_STYLES.map((style) => {
                        const isSelected = selectedStyles.includes(style.id);
                        return (
                          <button
                            key={style.id}
                            onClick={() => {
                              setSelectedStyles(prev => 
                                prev.includes(style.id)
                                  ? prev.filter(s => s !== style.id)
                                  : [...prev, style.id]
                              );
                            }}
                            className="px-4 py-2 rounded-full text-xs font-medium transition-all"
                            style={{
                              background: isSelected ? COLORS.primary : COLORS.borderLight,
                              color: isSelected ? '#ffffff' : COLORS.textSecondary,
                              border: `1px solid ${isSelected ? COLORS.primary : COLORS.border}`,
                            }}
                          >
                            {style.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* File Upload Section */}
            <div className="rounded-xl border" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
              <div className="p-4">
                <label className="text-xs font-semibold uppercase tracking-wide mb-3 block" style={{ color: COLORS.textSecondary }}>Upload Audio</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => document.getElementById('audio-upload-cover').click()}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: COLORS.borderLight, color: COLORS.textSecondary }}
                  >
                    <Upload className="w-3 h-3" />
                    Cover
                  </button>
                  <button
                    onClick={() => document.getElementById('audio-upload-extend').click()}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: COLORS.borderLight, color: COLORS.textSecondary }}
                  >
                    <Upload className="w-3 h-3" />
                    Extend
                  </button>
                  <button
                    onClick={() => document.getElementById('audio-upload-instrumental').click()}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: COLORS.borderLight, color: COLORS.textSecondary }}
                  >
                    <Music2 className="w-3 h-3" />
                    Add Inst.
                  </button>
                  <button
                    onClick={() => document.getElementById('audio-upload-vocals').click()}
                    className="px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-2"
                    style={{ background: COLORS.borderLight, color: COLORS.textSecondary }}
                  >
                    <Music className="w-3 h-3" />
                    Add Vocals
                  </button>
                </div>
                <input
                  id="audio-upload-cover"
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => handleAudioUpload(e, 'cover')}
                />
                <input
                  id="audio-upload-extend"
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => handleAudioUpload(e, 'extend')}
                />
                <input
                  id="audio-upload-instrumental"
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => handleAudioUpload(e, 'instrumental')}
                />
                <input
                  id="audio-upload-vocals"
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  onChange={(e) => handleAudioUpload(e, 'vocals')}
                />
              </div>
            </div>

            {/* Vocal/Instrumental Toggle */}
            <div className="flex gap-2 p-1 rounded-xl" style={{ background: COLORS.borderLight }}>
              <button
                onClick={() => setIsVocal(true)}
                className="flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-all"
                style={{
                  background: isVocal ? COLORS.surface : 'transparent',
                  color: isVocal ? COLORS.text : COLORS.textSecondary,
                  boxShadow: isVocal ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Vocal
              </button>
              <button
                onClick={() => setIsVocal(false)}
                className="flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-all"
                style={{
                  background: !isVocal ? COLORS.surface : 'transparent',
                  color: !isVocal ? COLORS.text : COLORS.textSecondary,
                  boxShadow: !isVocal ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                }}
              >
                Instrumental
              </button>
            </div>

            {/* Generate Button - directly below Vocal/Instrumental */}
            <div className="mt-4">
              <motion.button
                onClick={handleGenerate}
                disabled={(mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen}
                whileHover={(mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen ? {} : { scale: 1.01 }}
                whileTap={(mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen ? {} : { scale: 0.99 }}
                className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all"
                style={{
                  background: (mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen ? COLORS.border : COLORS.primary,
                  color: (mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen ? COLORS.textSecondary : '#ffffff',
                  cursor: (mode === 'simple' ? !prompt.trim() : !lyrics.trim()) || isGen ? 'not-allowed' : 'pointer',
                }}
              >
                <Sparkles className="w-4 h-4" />
                <span>{isGen ? 'Generating...' : 'Create'}</span>
              </motion.button>
            </div>

            {/* Inline progress (shown during generation) */}
            {isGen && progress && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 p-4 rounded-xl border"
                style={{ background: COLORS.accentLight, borderColor: COLORS.primary }}
              >
                <div className="flex items-center gap-3 mb-3">
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}>
                    <Sparkles className="w-4 h-4" style={{ color: COLORS.primary }} />
                  </motion.div>
                  <span className="text-xs font-semibold" style={{ color: COLORS.primary }}>{progress.msg}</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: COLORS.surface }}>
                  <motion.div
                    animate={{ width: `${progress.pct}%` }}
                    transition={{ duration: 0.5 }}
                    className="h-full rounded-full"
                    style={{ background: COLORS.primary }}
                  />
                </div>
              </motion.div>
            )}

          </div>
        </main>

        {/* Column 3: Main Canvas */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ background: COLORS.background }}>
          <header className="px-6 py-4 border-b flex items-center justify-between" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
              <input
                type="text"
                placeholder="Search"
                className="bg-transparent outline-none text-sm"
                style={{ color: COLORS.text }}
              />
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: COLORS.borderLight, color: COLORS.textSecondary }}>
                Newest
              </button>
              <button className="px-3 py-1.5 rounded-lg text-xs font-medium" style={{ color: COLORS.textSecondary }}>
                Liked
              </button>
              <button className="px-3 py-1.5 rounded-lg text-xs font-medium" style={{ color: COLORS.textSecondary }}>
                Public
              </button>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-0">

              {/* ── Skeletons always at TOP during generation (history stays visible below) ── */}
              {isGen && (
                <>
                  {[1, 2].map((idx) => (
                    <motion.div
                      key={`skeleton-${idx}`}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.08 }}
                      className="px-6 py-4 border-b flex items-center gap-4"
                      style={{ background: '#f5f3ff', borderColor: COLORS.border }}
                    >
                      <div className="w-16 h-16 rounded-lg animate-pulse" style={{ background: '#ede9fe' }} />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 rounded animate-pulse" style={{ background: '#ede9fe', width: '60%' }} />
                        <div className="h-3 rounded animate-pulse" style={{ background: '#ede9fe', width: '40%' }} />
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="h-3 w-12 rounded animate-pulse" style={{ background: '#ede9fe' }} />
                        <div className="w-8 h-8 rounded-full animate-pulse" style={{ background: '#ede9fe' }} />
                      </div>
                    </motion.div>
                  ))}
                  {progress && (
                    <div className="px-6 py-3 border-b" style={{ borderColor: COLORS.border, background: '#f5f3ff' }}>
                      <p className="text-xs font-semibold mb-1.5" style={{ color: COLORS.primary }}>{progress.msg}</p>
                      {progress.pct > 0 && (
                        <div className="w-full h-1 rounded-full overflow-hidden" style={{ background: '#ede9fe' }}>
                          <motion.div
                            className="h-full"
                            style={{ background: COLORS.primary }}
                            initial={{ width: 0 }}
                            animate={{ width: `${progress.pct}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Track list - always visible, even during generation ── */}
              {generations.filter(t => t.status !== 'generating').length === 0 && !isGen ? (
                <div className="flex items-center justify-center min-h-[300px]">
                  <div className="text-center p-12">
                    <Music className="w-16 h-16 mx-auto mb-4" style={{ color: COLORS.textTertiary }} />
                    <h4 className="text-lg font-semibold mb-2" style={{ color: COLORS.text }}>No music yet</h4>
                    <p className="text-sm" style={{ color: COLORS.textSecondary }}>Create your first track to get started</p>
                  </div>
                </div>
              ) : (
                generations.filter(t => t.status !== 'generating').map((track) => (
                  <MusicListCard
                    key={track.id}
                    track={track}
                    isPlaying={nowPlaying?.id === track.id && playing}
                    isSelected={selected?.id === track.id}
                    onPlay={() => playTrack(track)}
                    onSelect={() => setSelected(track)}
                    onAction={handleTrackAction}
                  />
                ))
              )}

              {/* Load More button */}
              {hasMore && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={async () => {
                      setLoadingMore(true);
                      await loadGens(dbOffset, true);
                      setLoadingMore(false);
                    }}
                    disabled={loadingMore}
                    className="px-6 py-2 rounded-xl text-sm font-medium transition-all"
                    style={{
                      background: COLORS.accentLight,
                      color: COLORS.primary,
                      border: `1px solid ${COLORS.primary}40`,
                      opacity: loadingMore ? 0.6 : 1,
                      cursor: loadingMore ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loadingMore ? 'Loading…' : 'Load More'}
                  </button>
                </div>
              )}

            </div>
          </div>

        </div>

        {/* Column 4: Right Panel - Lyrics (Conditional) */}
        {selected && (
          <aside className="w-[320px] flex-shrink-0 border-l flex flex-col overflow-hidden" style={{ background: COLORS.surface, borderColor: COLORS.border }}>
            <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold" style={{ color: COLORS.text }}>{selected.title || 'Untitled'}</h3>
                <button onClick={() => setSelected(null)}>
                  <X className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                </button>
              </div>
              <div className="w-full aspect-square rounded-xl overflow-hidden mb-4" style={{ background: COLORS.borderLight }}>
                {selected.video_url ? (
                  // Show music video player when a video has been generated
                  <video
                    src={selected.video_url}
                    controls
                    loop
                    className="w-full h-full object-cover"
                    poster={selected.image_url || undefined}
                    style={{ background: '#000' }}
                  />
                ) : selected.image_url ? (
                  <img src={selected.image_url} alt={selected.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full" style={{ background: getArtwork(selected.id || selected.title) }} />
                )}
              </div>
              {selected.video_url && (
                <div className="flex items-center gap-2 mb-2 px-1">
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: COLORS.primary + '18', color: COLORS.primary }}>🎬 Music Video</span>
                  <a href={selected.video_url} download={`${selected.title || 'music-video'}.mp4`} target="_blank" rel="noreferrer"
                    className="text-xs ml-auto" style={{ color: COLORS.textSecondary }}>
                    Download MP4
                  </a>
                </div>
              )}

              <p className="text-xs" style={{ color: COLORS.textSecondary }}>{selected.style || 'AI Generated'}</p>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: COLORS.textSecondary }}>
                  {timestampedLyrics ? 'Timestamped Lyrics' : 'Lyrics'}
                </h4>
                {(selected?.lyrics || timestampedLyrics) && (
                  <button
                    title="Copy lyrics"
                    onClick={() => {
                      const text = timestampedLyrics
                        ? timestampedLyrics.map(l => `${l.timestamp}  ${l.text}`).join('\n')
                        : selected?.lyrics || '';
                      navigator.clipboard.writeText(text).then(() => {
                        // Brief visual feedback via button title swap
                      }).catch(() => {});
                    }}
                    className="p-1 rounded-lg transition-all hover:opacity-70"
                    style={{ color: COLORS.textSecondary }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {timestampedLyrics ? (
                <div className="space-y-2 text-sm leading-relaxed" style={{ color: COLORS.text }}>
                  {timestampedLyrics.map((line, i) => (
                    <div key={i} className="flex gap-3">
                      <span className="text-xs font-mono" style={{ color: COLORS.textTertiary }}>
                        {line.timestamp}
                      </span>
                      <p>{line.text}</p>
                    </div>
                  ))}
                </div>
              ) : selected?.lyrics ? (
                <div className="space-y-2 text-sm leading-relaxed whitespace-pre-wrap" style={{ color: COLORS.text }}>
                  {selected.lyrics}
                </div>
              ) : (
                <div className="text-sm" style={{ color: COLORS.textSecondary }}>
                  <p>No lyrics available for this track.</p>
                </div>
              )}
            </div>
          </aside>
        )}
      </div>

      {/* Bottom Music Player - Spans 3 Columns (excluding sidebar) */}
      <AnimatePresence>
        {nowPlaying && (
          <motion.div
            key={`player-${nowPlaying.id}`}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-0 border-t"
            style={{ 
              left: '256px', 
              right: 0, 
              background: COLORS.surface, 
              borderColor: COLORS.border,
              zIndex: 9999,
              minHeight: '80px'
            }}
          >
            <div className="px-6 py-4 flex items-center justify-between gap-6">
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="w-12 h-12 rounded-xl overflow-hidden shadow-md">
                  {nowPlaying.image_url ? (
                    <img src={nowPlaying.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full" style={{ background: getArtwork(nowPlaying.id) }} />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate" style={{ color: COLORS.text }}>{nowPlaying.title || 'Untitled'}</p>
                </div>
              </div>

              <div className="flex flex-col items-center gap-3 flex-1 px-8">
                <div className="flex items-center gap-4">
                  <button className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                    <Shuffle className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                  </button>
                  <button className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                    <SkipBack className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                  </button>
                  <motion.button
                    onClick={togglePlay}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md transition-all"
                    style={{ background: COLORS.primary }}
                  >
                    {playing ? <Pause className="w-5 h-5 fill-white" /> : <Play className="w-5 h-5 fill-white ml-0.5" />}
                  </motion.button>
                  <button className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                    <SkipForward className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                  </button>
                  <button className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                    <Repeat className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                  </button>
                </div>

                <div className="w-full max-w-2xl flex items-center gap-3">
                  <span className="text-xs font-medium" style={{ color: COLORS.textSecondary }}>{fmt(currTime)}</span>
                  <div
                    className="flex-1 h-1.5 rounded-full relative overflow-hidden group cursor-pointer"
                    style={{ background: COLORS.borderLight }}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      if (audioRef.current) audioRef.current.currentTime = ((e.clientX - r.left) / r.width) * totalDur;
                    }}
                  >
                    <div className="absolute h-full rounded-full" style={{ background: COLORS.primary, width: `${pct}%` }} />
                    <div
                      className="absolute h-3 w-3 rounded-full top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity shadow-md"
                      style={{ background: COLORS.primary, left: `${pct}%`, transform: 'translate(-50%, -50%)' }}
                    />
                  </div>
                  <span className="text-xs font-medium" style={{ color: COLORS.textSecondary }}>{fmt(totalDur)}</span>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">
                <button
                  onClick={() => {
                    if (nowPlaying?.audio_url) {
                      const link = document.createElement('a');
                      link.href = nowPlaying.audio_url;
                      link.download = `${nowPlaying.title || 'music'}.mp3`;
                      link.target = '_blank';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                  className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                  title="Download"
                >
                  <Download className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                </button>
                <button onClick={() => setMuted(!muted)} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
                  {muted ? <VolumeX className="w-4 h-4" style={{ color: COLORS.textSecondary }} /> : <Volume2 className="w-4 h-4" style={{ color: COLORS.textSecondary }} />}
                </button>
                <div className="w-24 h-1.5 rounded-full" style={{ background: COLORS.borderLight }}>
                  <div className="h-full rounded-full" style={{ background: COLORS.primary, width: `${(muted ? 0 : vol) * 100}%` }} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {showGrid && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          zIndex: 9999,
        }}>
          {/* TOP RULER */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '30px',
            background: 'rgba(15, 23, 42, 0.95)',
            borderBottom: '2px solid #7c3aed',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}>
            {Array.from({ length: 100 }).map((_, i) => {
              const px = i * 20;
              const isMajor = i % 5 === 0;
              return (
                <div key={`top-${i}`} style={{
                  position: 'absolute',
                  left: `${px}px`,
                  height: isMajor ? '100%' : '50%',
                  borderLeft: `1px solid rgba(124, 58, 237, ${isMajor ? '0.6' : '0.3'})`,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '2px',
                }}>
                  {isMajor && (
                    <span style={{
                      fontSize: '9px',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                    }}>{px}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* LEFT RULER */}
          <div style={{
            position: 'absolute',
            top: '30px',
            left: 0,
            bottom: '30px',
            width: '30px',
            background: 'rgba(15, 23, 42, 0.95)',
            borderRight: '2px solid #7c3aed',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}>
            {Array.from({ length: 75 }).map((_, i) => {
              const px = i * 20;
              const isMajor = i % 5 === 0;
              return (
                <div key={`left-${i}`} style={{
                  position: 'absolute',
                  top: `${px}px`,
                  width: isMajor ? '100%' : '50%',
                  right: 0,
                  borderTop: `1px solid rgba(124, 58, 237, ${isMajor ? '0.6' : '0.3'})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: '2px',
                }}>
                  {isMajor && (
                    <span style={{
                      fontSize: '8px',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                      transform: 'rotate(-90deg)',
                      whiteSpace: 'nowrap',
                    }}>{px}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* RIGHT RULER */}
          <div style={{
            position: 'absolute',
            top: '30px',
            right: 0,
            bottom: '30px',
            width: '30px',
            background: 'rgba(15, 23, 42, 0.95)',
            borderLeft: '2px solid #7c3aed',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}>
            {Array.from({ length: 75 }).map((_, i) => {
              const px = i * 20;
              const isMajor = i % 5 === 0;
              return (
                <div key={`right-${i}`} style={{
                  position: 'absolute',
                  top: `${px}px`,
                  width: isMajor ? '100%' : '50%',
                  left: 0,
                  borderTop: `1px solid rgba(124, 58, 237, ${isMajor ? '0.6' : '0.3'})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingTop: '2px',
                }}>
                  {isMajor && (
                    <span style={{
                      fontSize: '8px',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                      transform: 'rotate(90deg)',
                      whiteSpace: 'nowrap',
                    }}>{px}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* BOTTOM RULER */}
          <div style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '30px',
            background: 'rgba(15, 23, 42, 0.95)',
            borderTop: '2px solid #7c3aed',
            display: 'flex',
            alignItems: 'center',
            pointerEvents: 'auto',
            overflow: 'hidden',
          }}>
            {Array.from({ length: 100 }).map((_, i) => {
              const px = i * 20;
              const isMajor = i % 5 === 0;
              return (
                <div key={`bottom-${i}`} style={{
                  position: 'absolute',
                  left: `${px}px`,
                  height: isMajor ? '100%' : '50%',
                  bottom: 0,
                  borderLeft: `1px solid rgba(124, 58, 237, ${isMajor ? '0.6' : '0.3'})`,
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '2px',
                }}>
                  {isMajor && (
                    <span style={{
                      fontSize: '9px',
                      color: '#a78bfa',
                      fontWeight: 'bold',
                    }}>{px}</span>
                  )}
                </div>
              );
            })}
          </div>

          {/* GRID LINES - Vertical and Horizontal */}
          <svg style={{
            position: 'absolute',
            top: '30px',
            left: '30px',
            right: '30px',
            bottom: '30px',
            width: 'calc(100% - 60px)',
            height: 'calc(100% - 60px)',
          }}>
            {/* Vertical lines every 20px */}
            {Array.from({ length: 100 }).map((_, i) => {
              const isMajor = i % 5 === 0;
              return (
                <line
                  key={`v-${i}`}
                  x1={i * 20}
                  y1={0}
                  x2={i * 20}
                  y2="100%"
                  stroke={isMajor ? 'rgba(124, 58, 237, 0.4)' : 'rgba(124, 58, 237, 0.15)'}
                  strokeWidth={isMajor ? '1' : '0.5'}
                  strokeDasharray={isMajor ? '4,4' : '2,2'}
                />
              );
            })}
            {/* Horizontal lines every 20px */}
            {Array.from({ length: 75 }).map((_, i) => {
              const isMajor = i % 5 === 0;
              return (
                <line
                  key={`h-${i}`}
                  x1={0}
                  y1={i * 20}
                  x2="100%"
                  y2={i * 20}
                  stroke={isMajor ? 'rgba(124, 58, 237, 0.4)' : 'rgba(124, 58, 237, 0.15)'}
                  strokeWidth={isMajor ? '1' : '0.5'}
                  strokeDasharray={isMajor ? '4,4' : '2,2'}
                />
              );
            })}
          </svg>

          {/* COLUMN GUIDES (12-column grid) */}
          <div style={{
            position: 'absolute',
            top: '30px',
            left: '294px',
            right: selected ? '350px' : '30px',
            bottom: '30px',
          }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={`col-${i}`}
                style={{
                  position: 'absolute',
                  left: `${(i / 12) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: `${(1 / 12) * 100}%`,
                  border: '1px solid rgba(236, 72, 153, 0.3)',
                  background: i % 2 === 0 ? 'rgba(236, 72, 153, 0.03)' : 'transparent',
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '40px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: '#ec4899',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '9px',
                  fontWeight: 'bold',
                }}>
                  {i + 1}
                </div>
              </div>
            ))}
          </div>

          {/* SIDEBAR HIGHLIGHT */}
          <div style={{
            position: 'absolute',
            left: '30px',
            top: '30px',
            width: '264px',
            bottom: '30px',
            border: '3px solid #7c3aed',
            background: 'rgba(124, 58, 237, 0.08)',
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#7c3aed',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}>
              Sidebar: 264px
            </div>
          </div>

          {/* CREATION PANEL HIGHLIGHT */}
          <div style={{
            position: 'absolute',
            left: '294px',
            top: '30px',
            width: '380px',
            bottom: '30px',
            border: '3px solid #10b981',
            background: 'rgba(16, 185, 129, 0.08)',
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: '#10b981',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}>
              Creation Panel: 380px
            </div>
          </div>

          {/* RIGHT PANEL HIGHLIGHT */}
          {selected && (
            <div style={{
              position: 'absolute',
              right: '30px',
              top: '30px',
              width: '320px',
              bottom: '30px',
              border: '3px solid #f59e0b',
              background: 'rgba(245, 158, 11, 0.08)',
            }}>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#f59e0b',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
              }}>
                Right Panel: 320px
              </div>
            </div>
          )}

          {/* MEASUREMENT LEGEND */}
          <div style={{
            position: 'fixed',
            top: '50px',
            right: '50px',
            background: 'rgba(15, 23, 42, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px',
            borderRadius: '12px',
            fontSize: '11px',
            color: 'white',
            pointerEvents: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            border: '2px solid #7c3aed',
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '12px', fontSize: '13px', color: '#a78bfa' }}>
              📐 Grid System
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                • Grid: 20px spacing
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                • Major lines: Every 100px
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                • Rulers: 20px increments
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                • Labels: Every 100px
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(203, 213, 225, 0.2)' }}>
                Sidebar: 264px
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                Creation: 380px
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                Right Panel: 320px
              </div>
              <div style={{ fontSize: '10px', color: '#cbd5e1' }}>
                Canvas: Cols 1-12
              </div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

// Helper Components
const NavItem = ({ icon: Icon, label, active, onClick, href }) => (
  <a
    href={href || '#'}
    onClick={onClick}
    className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm font-medium cursor-pointer"
    style={{
      color: active ? COLORS.primary : COLORS.textSecondary,
      background: active ? COLORS.accentLight : 'transparent',
    }}
  >
    <Icon className="w-5 h-5" />
    {label}
  </a>
);

const MusicListCard = ({ track, isPlaying, isSelected, onPlay, onSelect, onAction }) => {
  const [showMenu, setShowMenu] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Skeleton / generating state
  if (track.status === 'generating') {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl border"
        style={{ borderColor: COLORS.border, background: COLORS.surface, opacity: 0.7 }}
      >
        <div className="w-16 h-16 rounded-lg flex-shrink-0 animate-pulse" style={{ background: COLORS.borderLight }} />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="h-3 rounded animate-pulse w-3/4" style={{ background: COLORS.borderLight }} />
          <div className="h-2 rounded animate-pulse w-1/2" style={{ background: COLORS.borderLight }} />
          <div className="h-2 rounded animate-pulse w-1/4" style={{ background: COLORS.borderLight }} />
        </div>
        <div className="flex items-center gap-1 text-xs" style={{ color: COLORS.accent }}>
          <span className="animate-spin inline-block">⏳</span>
          <span>Generating…</span>
        </div>
      </div>
    );
  }



  return (
    <div
      onClick={onSelect}
      className="px-6 py-4 border-b flex items-center gap-4 cursor-pointer transition-colors hover:bg-gray-50"
      style={{
        background: isSelected ? COLORS.surfaceHover : COLORS.surface,
        borderColor: COLORS.border,
      }}
    >
      <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0" style={{ background: COLORS.borderLight }}>
        {track.image_url ? (
          <img src={track.image_url} alt={track.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: getArtwork(track.id || track.title) }} />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold truncate mb-1" style={{ color: COLORS.text }}>
          {track.title || 'Untitled'}
        </h4>
        <div className="flex items-center gap-1.5">
          <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
            {track.prompt || track.style || 'AI Generated'}
          </p>
          {track.video_url && (
            <span className="text-xs flex-shrink-0" title="Music video available">🎬</span>
          )}
        </div>
      </div>


      <div className="flex items-center gap-4 flex-shrink-0">
        <span className="text-xs" style={{ color: COLORS.textSecondary }}>
          {track.duration || '2:30'}
        </span>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPlay();
          }}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{
            background: isPlaying ? COLORS.primary : COLORS.borderLight,
            color: isPlaying ? '#ffffff' : COLORS.textSecondary,
          }}
        >
          {isPlaying ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current ml-0.5" />}
        </button>

        <button
          onClick={() => {
            if (track.audio_url) {
              const link = document.createElement('a');
              link.href = track.audio_url;
              link.download = `${track.title || 'music'}.mp3`;
              link.target = '_blank';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
          }}
          className="p-2 rounded-lg transition-colors hover:bg-gray-100"
          style={{ color: COLORS.textSecondary }}
          title="Download"
        >
          <Download className="w-4 h-4" />
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="p-2 rounded-lg transition-colors hover:bg-gray-100"
        >
          <Heart className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="p-2 rounded-lg transition-colors hover:bg-gray-100"
          >
            <MoreHorizontal className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
          </button>

          {showMenu && (
            <div
              className="absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg border z-50"
              style={{ background: COLORS.surface, borderColor: COLORS.border }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onAction(track, 'extend'); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                style={{ color: COLORS.text }}
              >
                <ArrowRight className="w-4 h-4" />
                Extend Track
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onAction(track, 'lyrics'); setShowMenu(false); }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                style={{ color: COLORS.text }}
              >
                <FileAudio className="w-4 h-4" />
                Get Lyrics
              </button>
              {/* Advanced actions require suno_audio_id (unavailable on older tracks) */}
              {['separate', 'wav', 'midi', 'video', 'cover'].map(act => {
                const labels   = { separate: 'Separate Vocals', wav: 'Convert to WAV', midi: 'Generate MIDI', video: 'Create Video', cover: 'Generate Cover' };
                const icons    = { separate: <Scissors className="w-4 h-4" />, wav: <AudioWaveform className="w-4 h-4" />, midi: <Music2 className="w-4 h-4" />, video: <Video className="w-4 h-4" />, cover: <Disc3 className="w-4 h-4" /> };
                const disabled = !track.suno_audio_id;
                return (
                  <button
                    key={act}
                    onClick={(e) => { if (disabled) return; e.stopPropagation(); onAction(track, act); setShowMenu(false); }}
                    className="w-full px-4 py-2 text-left text-sm flex items-center gap-2"
                    style={{ color: disabled ? COLORS.textTertiary : COLORS.text, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    title={disabled ? 'Regenerate this track to enable (requires Suno audio ID)' : undefined}
                  >
                    {icons[act]}
                    {labels[act]}
                    {disabled && <span className="ml-auto text-xs opacity-60">N/A</span>}
                  </button>
                );
              })}



              <div className="border-t" style={{ borderColor: COLORS.border }} />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (track.audio_url) {
                    const link = document.createElement('a');
                    link.href = track.audio_url;
                    link.download = `${track.title || 'music'}.mp3`;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
                style={{ color: COLORS.text }}
              >
                <Download className="w-4 h-4" />
                Download Track
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const FeaturedCard = ({ track, isPlaying, onPlay, onSelect }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={onSelect}
    className="col-span-8 group relative overflow-hidden rounded-[2rem] h-[400px] shadow-2xl cursor-pointer"
  >
    {track.image_url ? (
      <img src={track.image_url} alt={track.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
    ) : (
      <div className="w-full h-full transition-transform duration-700 group-hover:scale-105" style={{ background: getArtwork(track.id || track.title) }} />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#191c1e] via-transparent to-transparent opacity-90" />
    <div className="absolute bottom-0 left-0 p-10 w-full flex items-end justify-between">
      <div>
        <span className="px-3 py-1 text-white text-[10px] font-black uppercase rounded-full mb-4 inline-block" style={{ background: COLORS.primary }}>
          New Generation
        </span>
        <h4 className="text-white text-4xl font-black tracking-tighter" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {track.title || 'Untitled Track'}
        </h4>
        <p className="text-white/60 text-sm mt-2">{track.style || 'AI Generated'} • {track.duration || '-'}</p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onPlay();
        }}
        className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-xl hover:scale-110 transition-transform active:scale-95"
        style={{ color: COLORS.primary }}
      >
        {isPlaying ? <Pause className="w-8 h-8 fill-current" /> : <Play className="w-8 h-8 fill-current ml-1" />}
      </button>
    </div>
  </motion.div>
);

const CompactCard = ({ track, isPlaying, onPlay, onSelect }) => (
  <div
    onClick={onSelect}
    className="p-5 rounded-3xl border shadow-sm group transition-all cursor-pointer"
    style={{ background: COLORS.surfaceContainerLowest, borderColor: `${COLORS.outlineVariant}1a` }}
  >
    <div className="flex gap-4">
      <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
        {track.image_url ? (
          <img src={track.image_url} alt={track.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full" style={{ background: getArtwork(track.id || track.title) }} />
        )}
      </div>
      <div className="flex-1 py-1">
        <h5 className="text-sm font-bold mb-1">{track.title || 'Untitled'}</h5>
        <p className="text-[10px] font-medium mb-3" style={{ color: `${COLORS.onSurface}80` }}>
          {track.style || 'AI Generated'} • {track.duration || '-'}
        </p>
        <MiniWaveform seed={track.id || track.title} isActive={isPlaying} />
      </div>
    </div>
  </div>
);

const FeedCard = ({ track, isLiked, onLike, onSelect }) => (
  <div
    onClick={onSelect}
    className="rounded-[2.5rem] p-6 shadow-sm border transition-all cursor-pointer"
    style={{ background: COLORS.surfaceContainerLowest, borderColor: `${COLORS.outlineVariant}1a` }}
  >
    <div className="relative w-full aspect-square rounded-[2rem] overflow-hidden mb-6 shadow-lg">
      {track.image_url ? (
        <img src={track.image_url} alt={track.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full" style={{ background: getArtwork(track.id || track.title) }} />
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onLike();
        }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
        style={{ background: 'rgba(255, 255, 255, 0.6)', backdropFilter: 'blur(20px)', color: isLiked ? '#e11d48' : COLORS.onSurface }}
      >
        <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
      </button>
    </div>
    <h6 className="text-base font-bold mb-1">{track.title || 'Untitled'}</h6>
    <p className="text-xs mb-4" style={{ color: `${COLORS.onSurface}80` }}>
      @user • {track.created_at ? new Date(track.created_at).toLocaleDateString() : 'Recently'}
    </p>
    <div className="flex items-center justify-between">
      <div className="flex -space-x-2">
        <div className="w-6 h-6 rounded-full border-2 bg-slate-200" style={{ borderColor: COLORS.surface }} />
        <div className="w-6 h-6 rounded-full border-2 bg-slate-300" style={{ borderColor: COLORS.surface }} />
      </div>
      <span className="text-[10px] font-bold" style={{ color: `${COLORS.onSurface}66` }}>
        {track.play_count || 0} Plays
      </span>
    </div>
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="flex justify-between text-[11px] font-bold">
    <span style={{ color: `${COLORS.onSurface}66` }}>{label}</span>
    <span style={{ color: `${COLORS.onSurface}cc` }}>{value}</span>
  </div>
);

const PlayerBtn = ({ icon: Icon }) => {
  const [h, setH] = useState(false);
  return (
    <button onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} className="transition-colors">
      <Icon className="w-5 h-5" style={{ color: h ? COLORS.primary : `${COLORS.onSurface}99` }} />
    </button>
  );
};
